import React, { useCallback, useContext, useMemo } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { RootState } from '../../app/rootReducer';
import { SnackBarType } from '../../features/layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../features/layout/SnackbarContext';
import { AuthContext } from '../../features/login/AuthWrapper';
import { FeatureFlagsContext } from '../featureFlagsWrapper';
import { CaseLockAccess, computeCaseLockAccess } from './caseLockAccess';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const defaultCaseLockAccess: CaseLockAccess = {
  isFeatureEnabled: false,
  isExemptLifecycle: false,
  isCarrierUser: false,
  hasNonSubjectRole: false,
  isAssignedToCurrentUser: false,
  isUnassigned: false,
  isLockedForActions: false,
  bannerMessage: null,
};

export const CaseLockContext = React.createContext<CaseLockAccess>(
  defaultCaseLockAccess,
);

export function useCaseLockAccess(): CaseLockAccess {
  const auth = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const { underwriterId, applicationDetailedLifecycle } = useSelector(
    state => ({
      underwriterId: state.application.underwriterId,
      applicationDetailedLifecycle:
        state.application.applicationDetailedLifecycle,
    }),
  );

  return useMemo(
    () =>
      computeCaseLockAccess({
        isFeatureEnabled: flags.isCaseLockingEnabled === true,
        userId: auth.id,
        userRoles: auth.roles,
        underwriterId,
        applicationDetailedLifecycle,
      }),
    [
      flags.isCaseLockingEnabled,
      auth.id,
      auth.roles,
      underwriterId,
      applicationDetailedLifecycle,
    ],
  );
}

export function CaseLockProvider({
  value,
  children,
}: {
  value: CaseLockAccess;
  children: React.ReactNode;
}) {
  return (
    <CaseLockContext.Provider value={value}>
      {children}
    </CaseLockContext.Provider>
  );
}

export function useCaseLock(): CaseLockAccess {
  return useContext(CaseLockContext);
}

export function assertCaseLockActionAllowed(
  caseLock: Pick<CaseLockAccess, 'isLockedForActions' | 'bannerMessage'>,
  openSnack: (snack: {
    id: string;
    type: SnackBarType;
    message: string;
  }) => void,
): boolean {
  if (!caseLock.isLockedForActions) {
    return true;
  }
  openSnack({
    id: uuidv4(),
    type: SnackBarType.ALERT,
    message: caseLock.bannerMessage ?? 'This case is locked.',
  });
  return false;
}

/**
 * Returns a guard that blocks write actions when the case is locked.
 * Pass `caseLockOverride` when calling outside CaseLockProvider (e.g. Detail).
 */
export function useCaseLockActionGuard(
  caseLockOverride?: CaseLockAccess,
): () => boolean {
  const caseLockFromContext = useCaseLock();
  const caseLock = caseLockOverride ?? caseLockFromContext;
  const { openSnack } = useContext(SnackbarContext);

  return useCallback(
    () => assertCaseLockActionAllowed(caseLock, openSnack),
    [caseLock, openSnack],
  );
}
